import { Cloud, Download, Loader2, Monitor, Save, AlertTriangle, CheckCircle, Ban } from 'lucide-react';
import type { PosSettings } from '@/types';
import type { PosSyncMode } from '@/offline/db';
import { useSyncStore } from '@/store/syncStore';
import { isDesktopApp } from '@/desktop/electronApi';
import OfflineSyncPanel from '@/components/offline/OfflineSyncPanel';
import DesktopUnlockSettings from '@/components/desktop/DesktopUnlockSettings';

type DownloadItem = {
  id: string;
  label: string;
  detail: string;
  href?: string;
  filename?: string;
  available: boolean;
  note?: string;
};

/** Served from public/downloads (copied into dist on build). */
const DESKTOP_DOWNLOADS: DownloadItem[] = [
  {
    id: 'win',
    label: 'Windows (x64)',
    detail: 'Portable ZIP — cloud API + local store when offline (Local First)',
    href: '/downloads/SaleDesk-POS-Windows.zip',
    filename: 'SaleDesk-POS-Windows.zip',
    available: true,
  },
  {
    id: 'mac',
    label: 'macOS',
    detail: 'Disk image (.dmg)',
    available: false,
    note: 'Build on a Mac host — Coming soon',
  },
  {
    id: 'linux-appimage',
    label: 'Linux AppImage',
    detail: 'Portable AppImage',
    available: false,
    note: 'Coming soon',
  },
  {
    id: 'linux-deb',
    label: 'Linux Debian',
    detail: 'Package (.deb)',
    available: false,
    note: 'Coming soon',
  },
];

interface Props {
  posForm: PosSettings;
  setPosForm: (next: PosSettings) => void;
  setPosError: (msg: string) => void;
  onSave: (e: React.FormEvent) => void;
  isSaving: boolean;
  message: string;
  error: string;
}

export default function PosSyncModeTab({
  posForm,
  setPosForm,
  setPosError,
  onSave,
  isSaving,
  message,
  error,
}: Props) {
  const desktop = isDesktopApp();
  const mode = posForm.pos_sync_mode ?? 'Live Online';
  const isLocalMode =
    mode === 'Local First Auto Sync' || mode === 'Offline Always Manual Sync';

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Cloud className="w-5 h-5 text-primary-600" />
          POS Sync Mode
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {desktop
            ? 'This desktop app talks to Sale Desk cloud over API (no local Frappe). Choose Online POS, Offline first (auto sync), or Offline only (manual Sync Now).'
            : 'Choose how this company syncs between local offline store and the cloud.'}
        </p>
      </div>

      <form onSubmit={onSave} className="space-y-5">
        <div>
          <p className="font-medium text-gray-900 text-sm mb-1">Mode</p>
          <p className="text-xs text-gray-500 mb-2">
            Live Online = cloud APIs only. Local First = cloud when online, local when offline, auto-sync. Offline Always = local until Sync Now.
          </p>
          <select
            value={mode}
            onChange={(e) => {
              const next = e.target.value as PosSyncMode;
              const { pendingQueueCount, failedQueueCount } = useSyncStore.getState();
              const blocked = pendingQueueCount + failedQueueCount;
              if (next === 'Live Online' && blocked > 0) {
                const ok = window.confirm(
                  `There are ${blocked} unsynced queue item(s) (${pendingQueueCount} pending, ${failedQueueCount} failed).\n\nClick OK to discard the queue and switch to Live Online.\nClick Cancel, then use Sync Now or Discard Queue first.`,
                );
                if (!ok) {
                  setPosError(
                    `Cannot switch to Live Online while ${blocked} items are in the sync queue. Sync Now or Discard Queue first.`,
                  );
                  return;
                }
                void import('@/offline').then(async ({ discardAllQueueItems }) => {
                  await discardAllQueueItems();
                  setPosForm({ ...posForm, pos_sync_mode: next });
                  setPosError('');
                });
                return;
              }
              setPosForm({ ...posForm, pos_sync_mode: next });
              setPosError('');
            }}
            className="input-field w-full max-w-md"
          >
            <option value="Live Online">Live Online (Online POS)</option>
            <option value="Local First Auto Sync">Local First — Auto Sync</option>
            <option value="Offline Always Manual Sync">Offline Always — Manual Sync</option>
          </select>
        </div>

        {isLocalMode && (
          <div className="space-y-4 pt-2 border-t border-gray-100">
            <DesktopUnlockSettings />
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="font-medium text-gray-900 text-sm">Allow negative stock on sync</p>
                <p className="text-xs text-gray-500">When offline sales exceed server stock</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setPosForm({
                    ...posForm,
                    offline_sync_allow_negative_stock: !posForm.offline_sync_allow_negative_stock,
                  })
                }
                className={`px-4 py-1.5 text-xs font-semibold rounded-full border ${
                  posForm.offline_sync_allow_negative_stock
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}
              >
                {posForm.offline_sync_allow_negative_stock ? 'Enabled' : 'Disabled'}
              </button>
            </div>
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="font-medium text-gray-900 text-sm">Bypass shift gate offline</p>
                <p className="text-xs text-gray-500">Skip open-shift requirement in local modes</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setPosForm({
                    ...posForm,
                    offline_bypass_shift_in_local_modes: !posForm.offline_bypass_shift_in_local_modes,
                  })
                }
                className={`px-4 py-1.5 text-xs font-semibold rounded-full border ${
                  posForm.offline_bypass_shift_in_local_modes
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}
              >
                {posForm.offline_bypass_shift_in_local_modes ? 'Enabled' : 'Disabled'}
              </button>
            </div>
            <div className="flex items-center justify-between py-1 gap-4">
              <div>
                <p className="font-medium text-gray-900 text-sm">Offline invoice prefix</p>
                <p className="text-xs text-gray-500">Local invoice numbers e.g. OFF-20260728-00001</p>
              </div>
              <input
                className="input-field w-28"
                value={posForm.offline_invoice_prefix ?? 'OFF'}
                onChange={(e) =>
                  setPosForm({ ...posForm, offline_invoice_prefix: e.target.value.slice(0, 20) })
                }
              />
            </div>
            <OfflineSyncPanel />
          </div>
        )}

        {message && (
          <div className="flex items-center gap-2 text-emerald-700 text-sm">
            <CheckCircle className="w-4 h-4" /> {message}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}
        <button type="submit" disabled={isSaving} className="btn-primary flex items-center gap-2">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Sync Settings
        </button>
      </form>

      {!desktop && (
      <div className="pt-6 border-t border-gray-100">
        <div className="flex items-start gap-2 mb-3">
          <Monitor className="w-5 h-5 text-primary-600 mt-0.5" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">Desktop POS installers</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Cloud Frappe stays on the server. The Windows app uses APIs online and local storage when
              offline, then syncs when the connection returns.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {DESKTOP_DOWNLOADS.map((item) =>
            item.available && item.href ? (
              <a
                key={item.id}
                href={item.href}
                download={item.filename}
                className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 hover:border-primary-300 hover:bg-primary-50/40 transition-colors"
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-700">
                  <Download className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.detail}</p>
                </div>
              </a>
            ) : (
              <div
                key={item.id}
                className="flex items-center gap-3 rounded-xl border border-dashed border-gray-200 bg-gray-50 px-4 py-3 opacity-80"
              >
                <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                  <Ban className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-600">{item.label}</p>
                  <p className="text-xs text-gray-400">{item.note || item.detail}</p>
                </div>
              </div>
            ),
          )}
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          After download: unzip → open <strong>Sale Desk POS.exe</strong>. First launch needs internet
          for cloud login + catalog download.
        </p>
      </div>
      )}
    </div>
  );
}
